import React, { FunctionComponent, useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  InputAdornment,
  IconButton,
  Paper,
  Grid,
  Link,
  Alert,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

import { useGetUserLogin } from '../../hooks/api/auth';
import { useDispatch } from 'react-redux';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { setLogin } from '../../store/slices/auth';
import { IMAGES } from '../../utils/constants';
import { ACTIVE_COLOR, INACTIVE_COLOR } from '../../components/ui/formInput';
import StorageService from '../../services/storageService';

type ValidateField = {
  invalid: boolean;
  error: string;
};

type ValidateFieldsState = {
  email: ValidateField;
  password: ValidateField;
};

const LoginPage: FunctionComponent = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string>('');
  const [disabled, setDisabled] = useState<boolean>(false);

  const formValidation: ValidateFieldsState = {
    email: { invalid: false, error: '' },
    password: { invalid: false, error: '' },
  };
  const [validateFields, setValidateFields] = useState<ValidateFieldsState>(formValidation);

  const { mutateAsync: getUserLogin } = useGetUserLogin();

  const isValidEmail = (email: string) => {
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
    return emailRegex.test(email);
  };

  const validateForm = () => {
    let valid = true;
    const errors = { ...validateFields };

    if (!email.trim()) {
      errors.email = {
        invalid: true,
        error: 'Email is required',
      };
      valid = false;
    } else if (!isValidEmail(email)) {
      errors.email = {
        invalid: true,
        error: 'Please enter a valid email address',
      };
      valid = false;
    } else {
      errors.email = { invalid: false, error: '' };
    }
    if (!password.trim()) {
      errors.password = {
        invalid: true,
        error: 'Password is required',
      };
      valid = false;
    } else {
      errors.password = { invalid: false, error: '' };
    }
    setValidateFields(errors);
    return valid;
  };

  //const handleLogin = async (e: React.FormEvent) => {
  const handleLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setValidateFields(formValidation);
    if (!validateForm()) {
      return;
    }
    setDisabled(true);
    try {
      const result = await getUserLogin({ 
        email: email.toLowerCase().trim(),
        password: password
      });
      const loginData = result?.response?.data || result;
      
      // Check if this is a password reset response (has passwordResetToken but no accessToken)
      if (loginData && loginData.passwordResetToken && !loginData.accessToken) {
        // This is a password reset scenario (wrong password) - handle it directly
        navigate(`/reset-password?token=${loginData.passwordResetToken}`);
        return;
      }
      
      // Normal login validation
      if (!loginData || !loginData.accessToken) {
        setError('Invalid response from server');
        setDisabled(false);
        return;
      }
      StorageService.setTokens(loginData);
      setPassword('');
      
      dispatch(setLogin({
        user: { 
          userId: loginData.userId,
          email: loginData.email,
          accessLevel: loginData.accessLevel,
          company: loginData.company,
          lastLogin: loginData.lastLogin,
          username: loginData.username,
          temppassword: loginData.temppassword,
          passwordExpired: loginData.passwordExpired,
        },
        token: loginData.accessToken
      }));
      if (loginData.passwordExpired) {
        // Password expired - redirect to change password page
        StorageService.setForceTempPassword("true");
        navigate('/change-temp-password');
      } else if (loginData.temppassword) {
        // Check if user just changed their password
        const recentPasswordChange = StorageService.getRecentPasswordChange();
        
        if (recentPasswordChange === 'true') {
          // User just changed password, ignore temppassword flag
          // Clear the recent password change flag
          StorageService.clearRecentPasswordChange();
          
          // Proceed with normal login flow
          if (loginData.accessLevel === 'BARKSDALE') {
            navigate('/multiple-extranet');
          } else {
            navigate('/dashboard');
          }
        } else if (loginData.passwordResetToken && !loginData.accessToken) {
          // Password reset scenario (wrong password) - redirect to reset password page with token
          navigate(`/reset-password?token=${loginData.passwordResetToken}`);
        } else {
          // Normal temp password flow (correct password but TempPassword = 1)
          StorageService.setForceTempPassword("true");
          navigate('/change-temp-password');
        }
      } else {
        if (loginData.accessLevel === 'BARKSDALE') {
          navigate('/multiple-extranet');
         } 
        else {
          navigate('/dashboard');
        }
      }
    } catch (err: any) {
      setDisabled(false);
      if (err?.response?.data?.message) {
        setError(err.response.data.message);
      } else if (err?.message) {
        setError(err.message);
      } else {
        setError('An error occurred during login. Please try again.');
      }
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        backgroundColor: '#c3183c',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <Paper
        elevation={3}
        sx={{
          display: 'flex',
          width: 1000,
          minHeight: 520,
          overflow: 'visible',
          borderRadius: 2,
        }}
      >
        <Box
          sx={{
            width: '50%',
            backgroundImage: `linear-gradient(rgba(255, 0, 64, 0.4), rgba(255, 0, 64, 0.4)), url(${IMAGES.bannerImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />

        <Box
          sx={{
            width: '50%',
            bgcolor: '#f9f9f9',
            px: 6,
            py: 5,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          <form
            onSubmit={handleLogin}
            style={{ display: 'flex', flexDirection: 'column', width: '100%' }}
          >
          <Grid
            container
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 4 }}
          >
            <Grid>
              <img
                src={IMAGES.barksdaleLogo}
                alt="Barksdale Logo"
                style={{ height: 50 }}
              />
            </Grid>
            <Grid>
              <img
                src={IMAGES.extranetLogo}
                alt="Extranet Logo"
                style={{ height: 50 }}
              />
            </Grid>
          </Grid>

          <Typography variant="h6" gutterBottom>
            Extranet Logon
          </Typography>

          <TextField
            fullWidth
            label="Enter Email"
            variant="outlined"
            margin="normal"
            onChange={(e) => setEmail(e.target.value)}
            error={validateFields.email.invalid}
            helperText={validateFields.email.error}
            FormHelperTextProps={{ sx: { marginLeft: '0px' } }}
            InputLabelProps={{
              sx: {
                color: email ? ACTIVE_COLOR : INACTIVE_COLOR,
                "&.Mui-focused": {
                  color: ACTIVE_COLOR,
                },
                "&.MuiInputLabel-shrink": {
                  color: ACTIVE_COLOR,
                },
                "&:hover": {
                  color: ACTIVE_COLOR,
                },
              },
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                "&.Mui-focused fieldset": {
                  borderColor: ACTIVE_COLOR,
                },
                "&:hover fieldset": {
                  borderColor: ACTIVE_COLOR,
                },
              },
            }}
          />

          <TextField
            fullWidth
            label="Password"
            variant="outlined"
            margin="normal"
            type={showPassword ? 'text' : 'password'}
            onChange={(e) => setPassword(e.target.value)}
            error={validateFields.password.invalid}
            helperText={validateFields.password.error}
            FormHelperTextProps={{ sx: { marginLeft: '0px' } }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onMouseEnter={() => setShowPassword(true)}
                    onMouseLeave={() => setShowPassword(false)}
                    edge="end"
                  >
                    {showPassword ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            InputLabelProps={{
              sx: {
                color: password ? ACTIVE_COLOR : INACTIVE_COLOR,
                "&.Mui-focused": {
                  color: ACTIVE_COLOR,
                },
                "&.MuiInputLabel-shrink": {
                  color: ACTIVE_COLOR,
                },
                "&:hover": {
                  color: ACTIVE_COLOR,
                },
              },
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                "&.Mui-focused fieldset": {
                  borderColor: ACTIVE_COLOR,
                },
                "&:hover fieldset": {
                  borderColor: ACTIVE_COLOR,
                },
              },
            }}
          />

          <Typography variant="body2" sx={{ mt: 1, textAlign: 'right' }}>
            Forgot password?{' '}
            <Link href="/forgot-password" underline="hover" sx={{ color: '#c3183c', textDecoration: 'none' }}>
              Click Here.
            </Link>
          </Typography>
          
          {error && (
            (() => {
              if (error.includes('click here:')) {
                const parts = error.split('click here:');
                const beforeText = parts[0] || '';
                const afterPart = parts[1] || '';
                // Remove "/signup/initiate. " and keep the rest
                const afterText = afterPart.replace(/^[^.]*\.\s*/, '');
                // Split the afterText to make orders@barksdale.com a mailto link
                const emailRegex = /(orders@barksdale\.com)/;
                const emailParts = afterText.split(emailRegex);
                return (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    <Typography component="span">
                      {beforeText}
                      <Link href="/signup/initiate" sx={{ color: '#c3183c', textDecoration: 'underline', cursor: 'pointer', fontWeight: 500 }}>
                        click here
                      </Link>
                      {emailParts.map((part, index) => 
                        part === 'orders@barksdale.com' ? (
                          <Link key={index} href="mailto:orders@barksdale.com" sx={{ color: '#c3183c', textDecoration: 'none' }}>
                            orders@barksdale.com
                          </Link>
                        ) : (
                          <span key={index}>{part}</span>
                        )
                      )}
                    </Typography>
                  </Alert>
                );
              }
              return (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              );
            })()
          )}

          <Button
            variant="contained"
            sx={{ mt: 4, backgroundColor: '#c3183c' }}
            fullWidth
            //onClick={handleLogin}
            type="submit"
            disabled={disabled}
          >
            LOGON
          </Button>
          <Typography variant="body2" sx={{ mt: 1, textAlign: 'right' }}>
            Don't have an account?{' '}
            <Link href="/signup/initiate" underline="hover" sx={{ color: '#c3183c', textDecoration: 'underline' }}>
              Create One
            </Link>
          </Typography>
          </form>
        </Box>
      </Paper>
    </Box>
  );
};

export default LoginPage;
